import React, { useMemo } from "react";
import { Table, Typography } from "antd";
import type { ColumnType } from "antd/es/table";
import { LEDGER_CATEGORY, LedgerDto } from "dtos/ledger.dto";
import { formatMoney } from "utils/functions";
import { PaymentTable } from "dtos/strategy.dto";

function computeTotals(data: LedgerDto[]) {
  return data.reduce(
    (acc, row) => ({
      amount: acc.amount + row.amount,
      program_fee: acc.program_fee + row.distribution.program_fee,
      service_fee: acc.service_fee + row.distribution.service_fee,
      legal_fee: acc.legal_fee + row.distribution.legal_fee,
      creditor_total: acc.creditor_total + row.distribution.creditor_total,
    }),
    { amount: 0, program_fee: 0, service_fee: 0, legal_fee: 0, creditor_total: 0 },
  );
}

type LedgerRow = {
  key: string;
  category: LedgerDto["category"];
  week: number | string;
  total: number;
  program_fee: number;
  creditor_total: number;
  service_fee: number;
  legal_fee: number;
};

export type LedgerTableHeaders = "week" | "total" | "program_fee" | "service_fee" | "legal_fee" | "creditor_total";

interface LedgerTableProps {
  ledgerRows: LedgerDto[];
  paymentTable?: PaymentTable;
  mode: "detailed" | "compact";
  cycle: "weekly" | "monthly";
  initialPaymentRange?: number;
  interestPaymentRange?: number;
  loading?: boolean;
  headers?: LedgerTableHeaders[];
}

export const defaultHeaders: LedgerTableHeaders[] = [
  "week",
  "total",
  "program_fee",
  "service_fee",
  "legal_fee",
  "creditor_total",
];

export const LedgerTable: React.FC<LedgerTableProps> = ({
  ledgerRows,
  paymentTable,
  mode,
  cycle,
  initialPaymentRange,
  interestPaymentRange,
  loading,
  headers = defaultHeaders,
}) => {
  const columns: ColumnType<LedgerRow>[] = [
    {
      title: cycle === "monthly" ? "Month" : "Week",
      key: "week",
      dataIndex: "week",
      width: 60,
      render: (_, rec) => (
        <Typography.Text>{rec.category === LEDGER_CATEGORY.INITIAL ? "Initial" : rec.week}</Typography.Text>
      ),
    },
    {
      title: "Total",
      key: "total",
      dataIndex: "total",
      align: "right",
      render: (_, rec) => formatMoney(rec.total),
    },
    {
      title: "Program",
      key: "program_fee",
      dataIndex: "program_fee",
      align: "right",
      render: (_, rec) => formatMoney(rec.program_fee),
    },
    {
      title: "Service",
      key: "service_fee",
      dataIndex: "service_fee",
      align: "right",
      render: (_, rec) => formatMoney(rec.service_fee),
    },
    {
      title: "Legal",
      key: "legal_fee",
      dataIndex: "legal_fee",
      align: "right",
      render: (_, rec) => formatMoney(rec.legal_fee),
    },
    {
      title: "Creditor",
      key: "creditor_total",
      dataIndex: "creditor_total",
      align: "right",
      render: (_, rec) => formatMoney(rec.creditor_total),
    },
  ];

  const visibleColumns = useMemo(() => {
    return columns.filter((col) => col.dataIndex && headers.includes(col.dataIndex as LedgerTableHeaders));
  }, [columns, headers]);

  const totals = useMemo(() => {
    if (mode === "detailed") return computeTotals(ledgerRows);
    return {
      amount: paymentTable?.totals?.payment,
      program_fee: paymentTable?.totals?.program_fee,
      service_fee: paymentTable?.totals.service_fee,
      legal_fee: paymentTable?.totals.legal_fee,
      creditor_total: paymentTable?.totals.creditor,
    };
  }, [ledgerRows, paymentTable?.totals, mode]);

  const data: LedgerRow[] = useMemo(() => {
    const rows: LedgerRow[] = [];

    if (mode === "detailed") {
      for (const row of ledgerRows) {
        if (row.category === LEDGER_CATEGORY.SKIP) continue;
        rows.push({
          key: `${row.week}`,
          category: row.category,
          week: row.category === LEDGER_CATEGORY.MONTHLY ? row.week / 4 : row.week,
          total: row.amount,
          program_fee: row.distribution.program_fee,
          creditor_total: row.distribution.creditor_total,
          service_fee: row.distribution.service_fee,
          legal_fee: row.distribution.legal_fee,
        });
      }
    } else if (paymentTable) {
      rows.push({
        key: "initial",
        category: LEDGER_CATEGORY.INITIAL,
        week: paymentTable.first_row.week,
        total: paymentTable.first_row.payment,
        program_fee: paymentTable.first_row.program_fee,
        creditor_total: paymentTable.first_row.creditor,
        service_fee: paymentTable.first_row.service_fee,
        legal_fee: paymentTable.first_row.legal_fee,
      });

      paymentTable.repeat_rows.forEach(({ row, count }, idx) => {
        const start = cycle === "monthly" ? Math.ceil(row.week / 4) : row.week;
        let end: number | undefined = start + count - 1;
        if (cycle === "monthly") end = start + Math.floor(count / 4) - 1;
        if (end <= start) end = undefined;

        rows.push({
          key: `repeat-${idx}`,
          category: cycle === "monthly" ? LEDGER_CATEGORY.MONTHLY : LEDGER_CATEGORY.WEEKLY,
          week: end ? `${start}-${end}` : start,
          total: row.payment,
          program_fee: row.program_fee,
          creditor_total: row.creditor,
          service_fee: row.service_fee,
          legal_fee: row.legal_fee,
        });
      });
    }

    return rows;
  }, [ledgerRows, mode, paymentTable]);

  const summary = () => {
    if (loading) return null;
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <Typography.Text strong>Total</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} align="right">
            <Typography.Text strong>{formatMoney(totals.amount)}</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="right">
            <Typography.Text strong>{formatMoney(totals.program_fee)}</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right">
            <Typography.Text strong>{formatMoney(totals.service_fee)}</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            <Typography.Text strong>{formatMoney(totals.legal_fee)}</Typography.Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="right">
            <Typography.Text strong>{formatMoney(totals.creditor_total)}</Typography.Text>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <Table<LedgerRow>
      columns={visibleColumns}
      dataSource={loading ? [] : data}
      summary={summary}
      pagination={false}
      scroll={{ y: 250 }}
      size="small"
      bordered={false}
      rowKey="key"
      loading={loading}
      onRow={(rec) => {
        let idx = typeof rec.week === "string" ? Number(rec.week.split("-").at(-1)) : rec.week;
        if (rec.category === LEDGER_CATEGORY.MONTHLY) idx *= 4;

        let color = "#fefce8";
        if (initialPaymentRange && idx <= initialPaymentRange) {
          color = "#fdf4ff";
        } else if (interestPaymentRange && idx <= interestPaymentRange) {
          color = "#f0fdf4";
        }

        return {
          style: { background: color },
        };
      }}
    />
  );
};
